import { formatUSD } from "../lib/format";
import { ASSET_TYPE_LABEL, colorAt, fmtNumFr } from "../lib/portfolioMeta";

function Donut({ allocation }) {
  const R = 60;
  const C = 2 * Math.PI * R;
  let acc = 0;
  return (
    <svg viewBox="0 0 160 160" className="w-40 h-40 shrink-0" aria-hidden="true">
      {allocation.map((a, i) => {
        const len = (a.weight_pct / 100) * C;
        const seg = (
          <circle
            key={a.symbol}
            r={R}
            cx="80"
            cy="80"
            fill="none"
            stroke={colorAt(i)}
            strokeWidth="22"
            strokeDasharray={`${Math.max(0, len - 1.5)} ${C}`}
            strokeDashoffset={-acc}
            transform="rotate(-90 80 80)"
          />
        );
        acc += len;
        return seg;
      })}
      <text x="80" y="76" textAnchor="middle" fill="#a1a1aa" fontSize="10">
        {allocation.length} actif{allocation.length > 1 ? "s" : ""}
      </text>
      <text x="80" y="92" textAnchor="middle" fill="#fafafa" fontSize="13" fontWeight="700">
        100 %
      </text>
    </svg>
  );
}

/**
 * Composition du portefeuille (poids de chaque actif).
 * - allocation : [{symbol, asset_type, weight_pct}]
 * - positions  : admin uniquement → quantité, PRU et valeur affichés sous chaque ligne
 */
export default function PortfolioComposition({ allocation = [], positions = [], adminView = false }) {
  const posBySymbol = Object.fromEntries((positions || []).map((p) => [p.symbol, p]));
  return (
    <div className="rounded-xl border border-white/5 bg-card p-6" data-testid="sideb-composition">
      <h2 className="font-display font-bold text-lg mb-5">Composition</h2>
      {allocation.length === 0 ? (
        <div className="py-10 text-center text-sm text-zinc-500">Pas de positions ouvertes.</div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
          <Donut allocation={allocation} />
          <div className="flex-1 w-full">
            {allocation.map((a, i) => {
              const pos = posBySymbol[a.symbol];
              return (
                <div key={a.symbol} className="py-2.5 border-t border-white/5 first:border-0" data-testid={`sideb-position-${a.symbol}`}>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: colorAt(i) }} />
                    <span className="font-mono-num text-zinc-100 min-w-[3.5rem]">{a.symbol}</span>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                      {ASSET_TYPE_LABEL[a.asset_type] || a.asset_type}
                    </span>
                    <span className="ml-auto font-mono-num text-zinc-200">{fmtNumFr(a.weight_pct)} %</span>
                  </div>
                  <div className="mt-1.5 ml-5 h-1 rounded-full bg-white/5">
                    <div className="h-1 rounded-full" style={{ width: `${a.weight_pct}%`, background: colorAt(i) }} />
                  </div>
                  {adminView && pos && (
                    <div className="mt-1 ml-5 text-[11px] text-zinc-500 font-mono-num">
                      Qté {Number(pos.shares).toLocaleString("fr-FR", { maximumFractionDigits: 6 })} · PRU {formatUSD(pos.avg_cost)} · Prix{" "}
                      {formatUSD(pos.current_price)} · Valeur {formatUSD(pos.market_value)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {!adminView && (
        <p className="mt-4 text-[11px] text-zinc-600">
          Poids de chaque actif dans le portefeuille. Les montants investis ne sont pas publiés.
        </p>
      )}
    </div>
  );
}
